import * as React from "react";
import moment from "moment";
// import { Link } from "react-router-dom";
import { useMyAlert, useMarkAsSeen, useTrackAlertClick } from "@njoy/controller";
import { useSearchParams } from "react-router-dom";

import { TemplateSub } from "../../components/layouts/TemplateSub";
import Read from "../../assets/alerts/read.png";
import Unread from "../../assets/alerts/unread.png";

import Button from "../../components/uiMerge/newButton";
import AlertSkeleton from "./AlertSkeleton";

const headings = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "read", label: "Read" },
];

export const AlertsPage: React.FC = () => {
  const [currentPage, setCurrentPage] = React.useState<string>("all");
  const [readIds, setReadIds] = React.useState<string[]>([]);
  const trackedAlertIdRef = React.useRef<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const { alert = [], loading, hasMoreAlert, loadMore } = useMyAlert({ limit: 10, offset: 0 });
  const { markAsSeen } = useMarkAsSeen();
  const { trackAlertClick } = useTrackAlertClick();

  React.useEffect(() => {
    const alertId = searchParams.get("alertId");
    const pushProvider = searchParams.get("pushProvider") || undefined;
    if (!alertId || trackedAlertIdRef.current === alertId) {
      return;
    }

    trackedAlertIdRef.current = alertId;
    trackAlertClick({ id: alertId, provider: pushProvider }).catch(() => null);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("alertId");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams, trackAlertClick]);

  const uniqueAlerts = React.useMemo(() => {
    const seen = new Set<string>();
    return alert.filter((item: any) => {
      if (seen.has(item.id)) {
        return false;
      }

      seen.add(item.id);
      return true;
    });
  }, [alert]);

  const handleMarkAsRead = async (id: string) => {
    const pushProvider = searchParams.get("pushProvider") || undefined;
    await markAsSeen({ id, provider: pushProvider });
    setReadIds((prev) => [...prev, id]);
  };

  let filteredAlerts = uniqueAlerts;
  if (currentPage === "unread") {
    filteredAlerts = uniqueAlerts.filter((a: any) => !a.isSeen && !readIds.includes(a.id));
  } else if (currentPage === "read") {
    filteredAlerts = uniqueAlerts.filter((a: any) => a.isSeen || readIds.includes(a.id));
  }

  const sortedAlerts = [...filteredAlerts].sort((x: any, y: any) => {
    const timeY: any = new Date(y.createdAt);
    const timeX: any = new Date(x.createdAt);
    return timeY - timeX;
  });

  return (
    <TemplateSub page={`Alerts`}>
      <div className="w-full px-[1rem] flex-1 h-full overflow-y-auto pb-4">
        <div className="w-full flex gap-2 justify-center mt-2 items-center py-4 ">
          {headings.map((tab) => (
            <div
              key={tab.key}
              className={`text-[12px] shadow py-3 px-7 text-center w-full h-[42px] rounded-[10px] text-white cursor-pointer transition duration-300 ${currentPage === tab.key
                ? "bg-[linear-gradient(270deg,var(--brand-gradient-from)_0%,var(--brand-gradient-to)_98.3%)] font-semibold"
                : "bg-[#3C3E58]"
                }`}
              onClick={() => setCurrentPage(tab.key)}
            >
              {tab.label}
            </div>
          ))}
        </div>

        {loading ? (
          // <Loader align="center" center text="Fetching alerts..." />
          <>
            <div className="gap-3 mt-5 flex flex-col">
              <AlertSkeleton />
              <AlertSkeleton />
              <AlertSkeleton />
              <AlertSkeleton />
              <AlertSkeleton />
            </div>
          </>
        ) : !sortedAlerts.length ? (
          <div className="flex items-center justify-center px-2 gap-3 w-full p-4 mt-10 bg-[#1E1E1E] rounded-lg">
            <p className="text-[#B2B2B2] text-[14px] ">
              {/** If there are no alerts, ui displays you have not recieved any alerts. If you have alerts but none
                 * are read, ui displays you have no read alerts. If you have alerts but none are unread, ui displays 
                 * you have no unread alerts.
                */}
              {uniqueAlerts.length === 0
                ? "You have not received any alerts at the moment."
                : currentPage === "read"
                  ? "You have no read alerts."
                  : "You have no unread alerts."
              }
            </p>
          </div>
        ) : (
          sortedAlerts.map((a: any) => {
            const isRead = a.isSeen || readIds.includes(a.id);

            return (
              <div className="flex mt-3 items-start gap-3 w-full p-3 bg-[#1E1E1E] rounded-lg" key={a.id}>
                <img
                  src={isRead ? Read : Unread}
                  alt={isRead ? "read" : "unread"}
                  width={20}
                  height={20}
                  className="mt-1" />

                <div className="flex w-full flex-col gap-1">
                  {/* {(a.type === "Offer" || a.type === "Transaction") ?
                      <Link to={`/transaction-detail/${a.typeId}`}>
                        <p className="text-[#ffff] font-semibold text-[16px]">
                          {a.title}
                        </p>
                      </Link> : */}
                  <p className="text-[#ffff] font-semibold text-[16px]">
                    {a.title}
                  </p>
                  {/* } */}

                  <p className="text-[#B2B2B2] text-[14px] ">
                    {a.update}
                  </p>

                  <div className="flex justify-between items-center w-full">
                    <div className="flex items-center gap-1">
                      <p className="text-[#7777] text-[12px] ">
                        {moment(a.createdAt).format("Do MMM, YYYY")}{" "}|
                      </p>
                      <p className="text-[#7777] text-[12px]">
                        {moment(a.createdAt).format("h:mm A")}
                      </p>
                    </div>
                    <div className="flex gap-1 items-center">
                      {!isRead && (
                        <p
                          className="text-[#34C759] font-light text-[12px] cursor-pointer"
                          onClick={() => handleMarkAsRead(a.id)}>
                          Mark as read
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}

        {hasMoreAlert && (
          <React.Fragment>
            <div className="mt-auto mb-5 flex justify-center items-center pt-4">
              <Button
                className="bg-blue-600 mb-10 text-white !text-[14px] px-3 py-1 rounded"
                onClick={loadMore}
                disabled={loading}
              >
                Load More
              </Button>
            </div>
          </React.Fragment>
        )}
      </div>
    </TemplateSub>
  );
};